import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  normalizeExamQuestions,
  stripHtml,
  typesetMathJax,
} from './examReaderUtils';
import './ExamReaderPanel.css';

function countWords(text) {
  const t = String(text || '').trim();
  if (!t) return 0;
  return t.split(/\s+/).filter(Boolean).length;
}

function ExamReaderPanel({ questions, sourceLabel, onClose }) {
  const screenRef = useRef(null);
  const items = useMemo(() => normalizeExamQuestions({ questions }), [questions]);
  const [index, setIndex] = useState(0);
  const [selectedLetter, setSelectedLetter] = useState('');
  const [essayText, setEssayText] = useState('');
  const [showAnswers, setShowAnswers] = useState(true);

  useEffect(() => {
    setSelectedLetter('');
    setEssayText('');
  }, [index]);

  useEffect(() => {
    const el = screenRef.current;
    if (!el) return undefined;
    let cancelled = false;
    const run = () => {
      if (!cancelled) typesetMathJax(el);
    };
    run();
    const t1 = setTimeout(run, 200);
    const t2 = setTimeout(run, 500);
    return () => {
      cancelled = true;
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [index, showAnswers, items]);

  if (!items.length) {
    return (
      <div className="exam-reader-panel">
        <div className="exam-reader-toolbar">
          <span className="exam-reader-toolbar-title">Exam preview</span>
          <button type="button" className="exam-reader-close" onClick={onClose}>
            Close
          </button>
        </div>
        <p className="exam-reader-empty">
          No questions in this JSON yet. Compile database (Step 6), build a full exam, or load a quiz JSON with a questions array.
        </p>
      </div>
    );
  }

  const safeIndex = Math.min(index, items.length - 1);
  const q = items[safeIndex];
  const total = items.length;
  const correctOpt = q.options.find((o) => o.isCorrect);

  const go = (delta) => {
    setIndex((i) => Math.max(0, Math.min(total - 1, i + delta)));
  };

  const renderMcqOptions = () => (
    <div className="exam-reader-options">
      {q.options.map((opt) => {
        const isSelected = selectedLetter === opt.letter;
        const isCorrect = opt.isCorrect;
        let className = 'exam-reader-option';
        if (showAnswers && isCorrect) className += ' exam-reader-option--correct';
        if (showAnswers && isSelected && !isCorrect) className += ' exam-reader-option--wrong';
        if (!showAnswers && isSelected) className += ' exam-reader-option--selected';

        return (
          <button
            key={opt.letter}
            type="button"
            className={className}
            onClick={() => setSelectedLetter(opt.letter)}
          >
            <span className="exam-reader-option-letter">{opt.letter}</span>
            <span
              className="exam-reader-option-text studio-case-html-body mathjax-process"
              dangerouslySetInnerHTML={{ __html: opt.html }}
            />
          </button>
        );
      })}
    </div>
  );

  const renderEssayChoice = () => (
    <>
      <div className="exam-reader-essay-options">
        {q.options.map((opt) => {
          const isSelected = selectedLetter === opt.letter;
          return (
            <button
              key={opt.letter}
              type="button"
              className={`exam-reader-essay-option${isSelected ? ' exam-reader-essay-option--selected' : ''}`}
              onClick={() => setSelectedLetter(opt.letter)}
            >
              <span className="exam-reader-essay-label">{opt.letter}</span>
              <span
                className="studio-case-html-body mathjax-process"
                dangerouslySetInnerHTML={{ __html: opt.html }}
              />
            </button>
          );
        })}
      </div>
      <textarea
        className="exam-reader-textarea"
        placeholder={q.placeholder}
        value={essayText}
        onChange={(e) => setEssayText(e.target.value)}
        readOnly={false}
      />
      <div className="exam-reader-word-count">
        {countWords(essayText)} / {q.wordLimit} words
      </div>
      <button type="button" className="exam-reader-mark-btn" disabled title="Preview only">
        Mark
      </button>
    </>
  );

  const renderTextResponse = () => (
    <>
      <textarea
        className="exam-reader-textarea"
        placeholder={q.placeholder}
        value={essayText}
        onChange={(e) => setEssayText(e.target.value)}
      />
      <div className="exam-reader-word-count">
        {countWords(essayText)} / {q.wordLimit} words
      </div>
    </>
  );

  const renderFeedback = () => {
    if (!showAnswers) return null;
    if (q.kind === 'essay_choice' || q.kind === 'text_response') {
      if (!q.explanationHtml) return null;
      return (
        <div className="exam-reader-feedback">
          <div className="exam-reader-feedback-title">Model response</div>
          <div
            className="exam-reader-feedback-body studio-case-html-body mathjax-process"
            dangerouslySetInnerHTML={{ __html: q.explanationHtml }}
          />
        </div>
      );
    }
    if (!q.explanationHtml && !q.correctLetter) return null;
    return (
      <div className="exam-reader-feedback">
        {q.correctLetter ? (
          <div className="exam-reader-feedback-title">
            Correct Answer: {q.correctLetter}
          </div>
        ) : null}
        {correctOpt ? (
          <div className="exam-reader-feedback-correct">
            The correct answer is: {stripHtml(correctOpt.html)}
          </div>
        ) : null}
        {q.explanationHtml ? (
          <div
            className="exam-reader-feedback-body studio-case-html-body mathjax-process"
            dangerouslySetInnerHTML={{ __html: q.explanationHtml }}
          />
        ) : null}
        {q.workingSteps.length > 0 ? (
          <div className="exam-reader-working-steps">
            <h4>Step-by-step solution</h4>
            {q.workingSteps.map((step, i) => (
              <div
                key={`step-${i}`}
                className="exam-reader-step studio-case-html-body mathjax-process"
                dangerouslySetInnerHTML={{ __html: step }}
              />
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="exam-reader-panel">
      <div className="exam-reader-toolbar">
        <div>
          <span className="exam-reader-toolbar-title">Exam preview</span>
          {sourceLabel ? (
            <span className="exam-reader-toolbar-sub">{sourceLabel}</span>
          ) : null}
        </div>
        <div className="exam-reader-toolbar-actions">
          <label className="exam-reader-toggle">
            <input
              type="checkbox"
              checked={showAnswers}
              onChange={(e) => setShowAnswers(e.target.checked)}
            />
            Show answers &amp; explanations
          </label>
          <button
            type="button"
            className="exam-reader-nav"
            disabled={safeIndex <= 0}
            onClick={() => go(-1)}
          >
            Previous
          </button>
          <button
            type="button"
            className="exam-reader-nav"
            disabled={safeIndex >= total - 1}
            onClick={() => go(1)}
          >
            Next
          </button>
          <button type="button" className="exam-reader-close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      <div className="exam-reader-screen" ref={screenRef}>
        <div className="exam-reader-q-header">
          <span className="exam-reader-q-title">Question {safeIndex + 1}</span>
          <div className="exam-reader-q-meta">
            {q.badge ? (
              <span className="exam-reader-badge">{q.badge}</span>
            ) : null}
            <span className="exam-reader-q-progress">
              {safeIndex + 1} of {total}
            </span>
          </div>
        </div>

        {q.sectionHtml ? (
          <div
            className="exam-reader-section studio-case-html-body mathjax-process"
            dangerouslySetInnerHTML={{ __html: q.sectionHtml }}
          />
        ) : null}

        {q.exhibit.show ? (
          <div className="exam-reader-passage-card">
            <div
              className="exam-reader-passage-body studio-case-html-body mathjax-process"
              dangerouslySetInnerHTML={{ __html: q.exhibit.html }}
            />
          </div>
        ) : null}

        <div
          className="exam-reader-stem studio-case-html-body mathjax-process"
          dangerouslySetInnerHTML={{ __html: q.stemHtml || '<p></p>' }}
        />

        {q.kind === 'essay_choice' && renderEssayChoice()}
        {q.kind === 'text_response' && renderTextResponse()}
        {q.kind === 'multiple_choice' && renderMcqOptions()}

        {renderFeedback()}

        <div className="exam-reader-footer">
          <span>
            Question {safeIndex + 2 <= total ? safeIndex + 2 : safeIndex + 1}
          </span>
          <span>
            {safeIndex + 2 <= total
              ? `${safeIndex + 2} of ${total}`
              : `${safeIndex + 1} of ${total}`}
          </span>
        </div>
      </div>
    </div>
  );
}

export default ExamReaderPanel;
